import { Component, EffectRef, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription, catchError, finalize, interval, of, filter } from 'rxjs';
import { PageShellComponent, TabStripComponent, TabStripItem } from '@clinic-platform/ui-components';
import { AppointmentsService } from '../data-access/appointments.service';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { I18nService } from '../../../core/i18n/i18n.service';

@Component({
  selector: 'app-appointments-tabs-page',
  standalone: true,
  imports: [PageShellComponent, TabStripComponent, RouterOutlet],
  template: `
    <lib-page-shell>
      <lib-tab-strip
        [items]="tabs"
        [activeId]="activeTab"
        (activeIdChange)="onTabChange($event)"
        ariaLabel="Appointments tabs"
      />

      <router-outlet />
    </lib-page-shell>
  `,
})
export class AppointmentsTabsPageComponent implements OnInit, OnDestroy {
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly subscriptions = new Subscription();
  private readonly languageEffect: EffectRef;

  private readonly role: UserRole | null = this.resolveRole(this.authService.getCurrentRole());
  private readonly isSystemAdmin = this.role === 'system_admin';
  private isPendingSummaryLoading = false;
  private pendingSummaryRefreshQueued = false;

  protected tabs: TabStripItem[] = [];

  protected activeTab = 'schedule';

  public constructor() {
    this.languageEffect = effect(() => {
      this.i18n.language;
      this.refreshPendingIndicators();
    });
  }

  public ngOnInit(): void {
    this.updateActiveTab(this.router.url);
    this.refreshPendingIndicators();

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.updateActiveTab((event as NavigationEnd).urlAfterRedirects);
          this.refreshPendingIndicators();
        }),
    );

    this.subscriptions.add(
      interval(60000).subscribe(() => {
        this.refreshPendingIndicators();
      }),
    );

    if (this.isSystemAdmin) {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.subscribe(() => {
          this.refreshPendingIndicators();
        }),
      );
      this.subscriptions.add(
        this.systemAdminContext.scopeMode$.subscribe(() => {
          this.refreshPendingIndicators();
        }),
      );
    }
  }

  public ngOnDestroy(): void {
    this.languageEffect.destroy();
    this.subscriptions.unsubscribe();
  }

  protected onTabChange(tabId: string): void {
    if (!this.tabs.some((tab) => tab.id === tabId)) {
      return;
    }

    void this.router.navigate([tabId], { relativeTo: this.route });
  }

  private rebuildTabs(): void {
    const existingById = new Map(this.tabs.map((tab) => [tab.id, tab]));
    const items: TabStripItem[] = [{ id: 'schedule', label: this.i18n.translate('appointments.tabs.schedule') }];

    if (this.canAccessRescheduleTab()) {
      items.push({ id: 'reschedule', label: this.i18n.translate('appointments.tabs.reschedule') });
    }

    if (this.canAccessClientMapTab()) {
      items.push({ id: 'client-map', label: this.i18n.translate('appointments.tabs.clientMap') });
    }

    if (this.canAccessFluidCapacityTab()) {
      items.push({ id: 'fluid-capacity', label: this.i18n.translate('appointments.tabs.fluidCapacity') });
    }

    if (this.canAccessCapacityStageTab()) {
      items.push({ id: 'capacity-stage', label: this.i18n.translate('appointments.tabs.capacityStage') });
    }

    if (this.canAccessCapacityMatrixTab()) {
      items.push({ id: 'capacity-matrix', label: this.i18n.translate('appointments.tabs.capacityMatrix') });
    }

    if (this.canAccessChangeRequestsTab()) {
      items.push({ id: 'change-requests', label: this.i18n.translate('appointments.tabs.changeRequests') });
    }

    this.setTabsIfChanged(items.map((item) => {
      const existing = existingById.get(item.id);
      return existing
        ? { ...existing, label: item.label }
        : item;
    }));
  }

  private canAccessRescheduleTab(): boolean {
    return this.role === 'system_admin'
      || this.role === 'company_admin'
      || this.role === 'manager'
      || this.role === 'reception';
  }

  private canAccessClientMapTab(): boolean {
    return this.role === 'system_admin'
      || this.role === 'company_admin'
      || this.role === 'manager'
      || this.role === 'reception';
  }

  private canAccessChangeRequestsTab(): boolean {
    return this.role === 'system_admin'
      || this.role === 'company_admin'
      || this.role === 'manager'
      || this.role === 'reception';
  }

  private canAccessFluidCapacityTab(): boolean {
    return this.role === 'system_admin'
      || this.role === 'company_admin'
      || this.role === 'manager'
      || this.role === 'reception';
  }

  private canAccessCapacityStageTab(): boolean {
    return this.role === 'system_admin'
      || this.role === 'company_admin'
      || this.role === 'manager'
      || this.role === 'reception';
  }

  private canAccessCapacityMatrixTab(): boolean {
    return this.role === 'system_admin'
      || this.role === 'company_admin'
      || this.role === 'manager'
      || this.role === 'reception';
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    if (this.systemAdminContext.isGlobalScope()) {
      return false;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  private refreshPendingIndicators(): void {
    this.rebuildTabs();

    if (!this.hasRequiredCompanyContext()) {
      return;
    }

    if (this.isPendingSummaryLoading) {
      this.pendingSummaryRefreshQueued = true;
      return;
    }

    this.isPendingSummaryLoading = true;

    this.appointmentsService.getPendingSummary().pipe(
      catchError(() => of({
        scope: 'all' as const,
        schedule: { pendingCount: 0, hasPending: false },
        reschedule: { pendingCount: 0, hasPending: false },
        changeRequests: { pendingCount: 0, hasPending: false },
        totalPendingCount: 0,
        hasAnyPending: false,
      })),
      finalize(() => {
        this.isPendingSummaryLoading = false;
        if (this.pendingSummaryRefreshQueued) {
          this.pendingSummaryRefreshQueued = false;
          this.refreshPendingIndicators();
        }
      }),
    ).subscribe((summary) => {
      const schedulePending = summary.schedule.hasPending;
      const reschedulePending = this.canAccessRescheduleTab()
        ? summary.reschedule.hasPending
        : false;
      const changeRequestsPending = this.canAccessChangeRequestsTab()
        ? summary.changeRequests.hasPending
        : false;

      const tabs = this.tabs.map((tab) => {
        const hasPending = (
          (tab.id === 'schedule' && schedulePending)
          || (tab.id === 'reschedule' && reschedulePending)
          || (tab.id === 'change-requests' && changeRequestsPending)
        );

        return {
          ...tab,
          dot: hasPending,
          dotTitle: hasPending ? 'Pending items' : undefined,
          dotColor: hasPending ? '#ef4444' : undefined,
          dotRingColor: hasPending ? 'rgba(239, 68, 68, 0.35)' : undefined,
          dotBlinking: hasPending,
          dotBlinkPeriodMs: hasPending ? 1200 : undefined,
        } satisfies TabStripItem;
      });
      this.setTabsIfChanged(tabs);
    });
  }

  private setTabsIfChanged(nextTabs: TabStripItem[]): void {
    if (this.areTabsEqual(this.tabs, nextTabs)) {
      return;
    }

    this.tabs = nextTabs;
  }

  private areTabsEqual(left: TabStripItem[], right: TabStripItem[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => {
      const other = right[index];
      return item.id === other.id
        && item.label === other.label
        && item.disabled === other.disabled
        && item.dot === other.dot
        && item.dotTitle === other.dotTitle
        && item.dotColor === other.dotColor
        && item.dotRingColor === other.dotRingColor
        && item.dotBlinking === other.dotBlinking
        && item.dotBlinkPeriodMs === other.dotBlinkPeriodMs;
    });
  }

  private updateActiveTab(url: string): void {
    const clean = (url ?? '').split('?')[0];
    if (clean.includes('/appointments/reschedule')) {
      this.activeTab = 'reschedule';
      return;
    }
    if (clean.includes('/appointments/client-map')) {
      this.activeTab = 'client-map';
      return;
    }
    if (clean.includes('/appointments/change-requests')) {
      this.activeTab = 'change-requests';
      return;
    }
    if (clean.includes('/appointments/capacity-stage')) {
      this.activeTab = 'capacity-stage';
      return;
    }
    if (clean.includes('/appointments/capacity-matrix')) {
      this.activeTab = 'capacity-matrix';
      return;
    }
    if (clean.includes('/appointments/fluid-capacity')) {
      this.activeTab = 'fluid-capacity';
      return;
    }
    this.activeTab = 'schedule';
  }

  private resolveRole(role: string | null): UserRole | null {
    switch (role) {
      case 'system_admin':
      case 'company_admin':
      case 'manager':
      case 'technician':
      case 'specialist':
      case 'reception':
      case 'client':
        return role;
      default:
        return null;
    }
  }
}
